import { Button, BoxV2 as Box, Table, useTable, Flex } from 'portal-commons';
import React, { useState, useMemo, useRef } from 'react';
import { faKey } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import ApiKeyCreatedModal from './ApiKeyCreatedModal';
import ApiKeyListingRow from './ApiKeyListingRow';
import { ApiAccount } from '../types';
import { globalGetService, globalPostService } from '@/utils/globalApiServices';

const headRows = [
  { id: 'displayName', label: 'API LABEL', sortable: false },
  { id: 'apiKey', label: 'API KEY', sortable: false },
  { id: 'updateDate', label: 'LAST UPDATED', sortable: false },
  { id: 'enabled', label: 'ACTIVE', sortable: false },
  { id: 'actions', label: 'ACTIONS', sortable: false },
];

const PAGE_SIZE = 10;

export default function ApiKeysListing() {
  const [addNewApiKey, setAddNewApiKey] = useState(false);
  const [activeAccount, setActiveAccount] = useState<ApiAccount>();
  const [addNewApiKeyLoader, setAddNewApiKeyLoader] = useState(false);
  const skipFetchRef = useRef(false);
  const remoteListRef = useRef<ApiAccount[]>([]);

  const table = useTable<ApiAccount>(
    async (filter) => {
      if (skipFetchRef.current) {
        const data = remoteListRef.current;
        return {
          data,
          page: filter?.page || 1,
          rowsPerPage: PAGE_SIZE,
          total: data.length,
        };
      }
      const list = await globalGetService(`mno/api/account`).then(
        (response) => {
          const list = response?.data || [];
          skipFetchRef.current = !!response?.ok;
          remoteListRef.current = list;
          return list;
        }
      );

      return {
        data: list,
        page: filter?.page || 1,
        rowsPerPage: PAGE_SIZE,
        total: list.length,
      };
    },
    {
      deps: [],
      inMemory: true,
    }
  );

  const currentPage = table.pagination.page;

  const reloadTable = () => {
    skipFetchRef.current = false;
    table.reload();
  };

  const handleAddNewApiKey = () => {
    setAddNewApiKeyLoader(true);
    globalPostService(`mno/api/account`, {}).then((response) => {
      setAddNewApiKeyLoader(false);
      if (response?.ok) {
        setActiveAccount(response.data);
        setAddNewApiKey(true);
        reloadTable();
      }
    });
  };

  const displayAccounts = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return table.tableData.slice(start, end);
  }, [table.tableData, currentPage]);

  return (
    <>
      <div className="api-keys-listing-wrapper" data-testid="apiKeysListing">
        <div className="title-blk">
          <h3 className="flex heading1 space-between v-center">
            <div>
              <Flex sx={{ flexDirection: 'row', justifyContent: 'center' }}>
                <Box sx={{ mr: 'xs' }}>
                  <FontAwesomeIcon icon={faKey} />
                </Box>
                <div>API Keys</div>
              </Flex>
            </div>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                right: 0,
                transform: 'translateY(-50%)',
              }}
            >
              <Button
                data-testid="apiKeysListingAddButton"
                disabled={addNewApiKeyLoader}
                portal="mno"
                shape="square"
                color="tertiary"
                size="small"
                onClick={handleAddNewApiKey}
              >
                Create API Key
              </Button>
            </Box>
          </h3>
        </div>
        <Table
          disableHover
          loading={table.loading}
          className="api-keys-listing-table"
          emptyState=""
          headRows={headRows}
          tableData={displayAccounts.map((data) => (
            <ApiKeyListingRow
              accountObj={data}
              key={data.id}
              fetchApiAccounts={reloadTable}
            />
          ))}
          handleChangePage={table.handleChangePage}
          filter={table.filter}
          pagination={table.pagination}
        />
      </div>
      <ApiKeyCreatedModal
        open={addNewApiKey}
        account={activeAccount}
        onClose={() => {
          setAddNewApiKey(false);
          setActiveAccount(undefined);
        }}
      />
    </>
  );
}
